import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, RouteComponentProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Loader from 'components/atoms/loader';

import { setupSoundSubscription, setupAcademyLiveSubscription } from 'services/cable';
import { getLiveNow, setLiveNow } from 'store/modules/academy_store/actions';
import { ApplicationState } from 'store';

import SignIn from 'pages/SignIn';
import Maintenance from 'pages/Maintenance';
import Painel from 'pages/Painel';
import Help from 'pages/Help';
import Monitoring from 'pages/MonitoringService';
import MonitoringConfiguration from 'pages/MonitoringConfiguration';
import WarantyPhrases from 'pages/WarantyPhrases';
import BiddingOnlineSearch from 'pages/BiddingOnlineSearch';
import Management from 'pages/Management';
import { Contracts } from 'pages/Contracts';
import { AtaRegistrationPrice } from 'pages/AtaRegistrationPrice';
import IminenciaDeserted from 'pages/IminenciaDeserted';
import PlanosPrecos from 'pages/PlanosPrecos';
import PlansList from 'pages/PlansList';
import { Contact } from 'pages/Contact';
import { CardSuccess } from 'pages/CardSuccess';
import { SaleSuccess } from 'pages/SaleSuccess';
import { SolicitacaoEdital } from 'pages/SolicitacaoEdital';
import { Checkout } from 'pages/Checkout';
import { EditContact } from 'pages/EditContact';
import RoboLanceSale from 'pages/RoboLanceSale';
import MyHistory from 'pages/MyHistory';
import DigitalSignature from 'pages/DigitalSignature';
import BiddingDetails from 'pages/BiddingDetails';
import Bulletin from 'pages/Bulletin';
import RoboDashboard from 'pages/RoboDashboard';
import RobotUserDetails from 'components/organisms/robot_user_details';
import BulletinContent from 'pages/BulletinContent';
import MatchLicitacoes from 'pages/MatchLicitacoes';
import MyAccount from 'pages/MyAccount';
import { Course } from 'pages/Course';
import FollowupOnlineSearch from 'pages/FollowupOnlineSearch';
import { Conlicitantes } from 'pages/Conlicitantes';
import Documents from 'pages/DocumentsService';
import { Market } from 'pages/Market';
import JobVacancy from 'pages/JobVacancy';
import BiddingMarket from 'pages/bidding_market';
import CadastralAdvisory from 'pages/cadastralAdvisory';
import ProgramaDeIntegridade from 'pages/ProgramaDeIntegridade';
import ConlicitacaoApi from 'pages/ConlicitacaoApi';
import ConlicitacaoApiRequest from 'pages/ConlicitacaoApiRequest';
import ApiRequestPresentation from 'pages/ConlicitacaoApiRequest/Presentation';
import MeusTokens from 'pages/MeusTokens';
import ConlicitaGo from 'pages/ConlicitaGo';
import { ToolsStatus } from 'pages/ToolsStatus';
import JuridicoFacil from 'pages/JuridicoFacil';
import JuridicoFacilShow from 'pages/JuridicoFacilShow';
import JuridicoThemes from 'pages/JuridicoThemes';
import JuridicoSearch from 'pages/JuridicoSearch/Index';
import Legislation from 'pages/Legislation';
import LegislationLaw from 'pages/LegislationLaw';
import LegislationSearch from 'components/organisms/legislation_search';
import ConsultancyLegal from 'pages/ConsultancyLegal';
import { DicaPost } from 'pages/DicaPost';
import Register from 'pages/Register';
import Passwords from 'pages/Passwords';
import BiddingNotificationDetails from 'pages/BiddingNotificationDetails';
import SharedBidding from 'pages/SharedBidding';
import DocumentGenerator from 'pages/DocumentGenerator';
import Notifications from 'pages/Notifications';
import Invitation from 'pages/Invitation';
import { Expired } from 'pages/Expired';
import BiddingDetailsPrBw from 'pages/BiddingDetailsPrBw';
import MonitoringQuoted from 'pages/MonitoringQuoted';
import DisabledService from 'components/molecules/disabled_service';
import BlockedSubscription from 'components/molecules/blocked_subscription';
import CampaignRenewal from 'pages/CampaignRenewal';
import RenewalEmail from 'pages/RenewalEmail';
import RegistrationFlow from 'pages/RegistrationFlow';
import DrLicita from 'pages/DrLicita';
import DrLicitaChat from 'pages/DrLicitaChat';
import DrLicitaCreateDocument from 'pages/DrLicitaCreateDocument';
import DrLicitaPeticoes from 'pages/DrLicitaPeticoes';
import DrLicitaProjetos from 'pages/DrLicitaProjetos';
import DrLicitaJurisprudencia from 'pages/DrLicitaJurisprudencia';
import DrLicitaBiblioteca from 'pages/DrLicitaBiblioteca';
import Portals from 'pages/Portals';
import Palestra from 'pages/palestra';
import ManagementConfiguration from 'pages/ManagementConfiguration';
import { ConcurrentsNew } from 'pages/ConcurrentsNew';
import ConcurrentsData from 'components/organisms/concurrents_data';
import Conlicitaseg from 'pages/Conlicitaseg';
import ProcessInfo from 'components/atoms/process_info';
import ReasonsNewInsurance from 'components/atoms/reasons_new_insurance';
import QuoteContractInfo from 'components/atoms/quote_contract_info';
import BiddingQuoteIntro from 'components/atoms/bidding_quote_intro';
import QuoteHolderData from 'components/atoms/quote_holder_data';
import BiddingQuoteContractUpload from 'components/atoms/bidding_quote_contract_upload';
import BiddingQuoteStart from 'components/atoms/bidding_quote_start';
import QuotesSelection from 'components/atoms/quotes_selection';
import InsuranceSuccess from 'components/atoms/insurance_success';
import RegisterNew from 'pages/RegisterNew';
import Onboarding from 'pages/Onboarding';
import RegisterPendingApproval from 'pages/RegisterPendingApproval';
import RegisterPreparing from 'pages/RegisterPreparing';
import PaymentInfo from 'components/atoms/payment_info';
import QuoteConfimation from 'components/atoms/quote_confirmation';
import ConlicitasegData from 'components/atoms/conlicitaseg_data';
import ContractValidator from 'pages/ContractValidator';
import DetailedContractAnalysis from 'pages/DetailedContractAnalysis';
import NewContractAnalysis from 'pages/NewContractAnalysis';
import { ConlicitasegContact } from 'pages/ConlicitasegContact';
import ConlicitasegOnlyOnboarding from 'pages/ConlicitasegOnlyOnboarding';
import BulletinData from 'pages/BulletinData';
import CentralIA from 'pages/CentralIA';
import { canAccessCentralIa, canAccessContractClauses } from 'services/alfa_tools';
import Academy from 'pages/Academy';
import AmbassadorInvitation from 'pages/Ambassador/Invitation';
import AmbassadorToolDetail from 'pages/Ambassador/ToolDetail';
import AmbassadorToolFeedback from 'pages/Ambassador/ToolFeedback';
import PriceIntelligence from 'pages/PriceIntelligence';
import PriceIntelligenceSaved from 'pages/PriceIntelligenceSaved';
import PriceIntelligenceSavedDetail from 'pages/PriceIntelligenceSaved/Detail';
import AcademyFloatingVideo from 'components/organisms/academy_floating_video';
import { AIManagement } from 'pages/AIManagement';
import ConlicitasegSignup from 'pages/ConlicitasegSignup';
import ConlicitasegEmailValidation from 'pages/ConlicitasegEmailValidation';
import { withToolAccess } from './with_tool_access';
import Route from './Route';
import LegacyQuoteRedirects from './legacy_quote_redirects';

const RoboDeLances = lazy(() => import('pages/RoboDeLances'));
const RepresentantClients = lazy(() => import('pages/RepresentantClients'));
const RepresentantKanbanPage = lazy(() => import('pages/RepresentantKanban'));
const RepresentantProspectingPage = lazy(() => import('pages/RepresentantProspecting'));
const RepresentantSuppliersPage = lazy(() => import('pages/RepresentantSuppliers'));
const RepresentationRequest = lazy(() => import('pages/RepresentationRequest'));

// Ferramentas em fase alfa: a rota só abre para alfa ou para o embaixador em teste.
const GuardedCentralIA = withToolAccess(CentralIA, canAccessCentralIa);
const GuardedContractValidator = withToolAccess(ContractValidator, canAccessContractClauses);
const GuardedNewContractAnalysis = withToolAccess(NewContractAnalysis, canAccessContractClauses);
const GuardedDetailedContractAnalysis = withToolAccess(
  DetailedContractAnalysis,
  canAccessContractClauses,
);

export default function Routes() {
  const dispatch = useDispatch();
  const profileId = useSelector((state: ApplicationState) => state.storage.profile.profile.id);
  // Conlicitação Academy em fase alfa: só ativa para clientes alfa_testers.
  const academyEnabled = useSelector(
    (state: ApplicationState) => state.storage.profile.profile.alfa_tester,
  );

  useEffect(() => {
    let unlocked = false;
    const unlockAudio = () => {
      if (unlocked) return;
      const audio: any = document.getElementById('aviso_sonoro');
      if (audio) {
        audio.muted = true;
        audio
          .play()
          .then(() => {
            audio.pause();
            audio.muted = false;
            audio.currentTime = 0;
            unlocked = true;
          })
          .catch(() => {});
      }
    };

    const events = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach((evt) => document.addEventListener(evt, unlockAudio));
    return () => {
      events.forEach((evt) => document.removeEventListener(evt, unlockAudio));
    };
  }, []);

  useEffect(() => {
    if (!profileId) return;
    setupSoundSubscription(profileId);
    if (!academyEnabled) return;
    dispatch(getLiveNow());
    setupAcademyLiveSubscription((live) => dispatch(setLiveNow(live)));
  }, [profileId, academyEnabled]);

  return (
    <BrowserRouter>
      <Suspense fallback={<Loader />}>
        <Route path="/" exact component={SignIn} />
        <Route path="/manutencao" exact component={Maintenance} />
        <Route path="/signup=new" exact component={SignIn} />
        <Route path="/painel" component={Painel} isPrivate />
        <Route path="/boletim_web/public/boletins" exact component={Bulletin} isPrivate />
        <Route
          path="/boletim_web/public/boletins/:id"
          exact
          component={BulletinContent}
          isPrivate
        />
        <Route
          path="/boletim_web/public/boletins/:id/open"
          exact
          component={BulletinContent}
          isPrivate
        />
        <Route
          path="/boletim_web/public/boletins/new/:id/match"
          exact
          component={MatchLicitacoes}
          isPrivate
        />
        <Route
          path="/boletim_web/public/boletins/:id/match"
          exact
          component={MatchLicitacoes}
          isPrivate
        />
        <Route
          path="/boletim_web/public/boletins/new/:id"
          exact
          component={BulletinData}
          isPrivate
        />
        <Route path="/documentos" component={Documents} isPrivate />
        <Route path="/facilitadores/monitoramento" component={Monitoring} isPrivate />
        <Route
          path="/facilitadores/trading_keywords"
          component={MonitoringConfiguration}
          isPrivate
        />
        <Route path="/banco_de_dados" exact component={BiddingOnlineSearch} />
        <Route path="/banco_de_dados/acompanhamentos" component={FollowupOnlineSearch} />
        <Route path="/minhas_licitacoes/management" exact component={Management} />
        <Route
          path="/minhas_licitacoes/management/:id/:currentCompany"
          exact
          component={BiddingDetails}
          isPrivate
        />
        <Route
          path="/minhas_licitacoes/configuracoes"
          exact
          component={ManagementConfiguration}
          isPrivate
        />
        <Route
          path="/biddings/:id"
          component={(routerProps: RouteComponentProps) => (
            <BiddingNotificationDetails params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/ajuda" component={Help} isPrivate />
        <Route path="/contratos" component={Contracts} isPrivate />
        <Route path="/ata_de_registro_preco" component={AtaRegistrationPrice} isPrivate />
        <Route path="/contato" component={Contact} isPrivate />
        <Route path="/iminencia" component={IminenciaDeserted} isPrivate />
        <Route path="/finalizar_assinatura" component={PlanosPrecos} isPrivate />
        <Route path="/planos" component={PlansList} isPrivate />
        <Route path="/compra_finalizada" component={CardSuccess} isPrivate />
        <Route path="/compra_realizada" component={SaleSuccess} isPrivate />
        <Route path="/waranty_phrases" component={WarantyPhrases} isPrivate />
        <Route path="/solicitar_edital" component={SolicitacaoEdital} isPrivate />
        <Route path="/checkout" component={Checkout} isPrivate />
        <Route path="/editar_contato" component={EditContact} isPrivate />
        <Route path="/robo_lance" component={RoboLanceSale} isPrivate />
        <Route path="/meu_historico" component={MyHistory} isPrivate />
        <Route path="/assinatura_digital" component={DigitalSignature} isPrivate />
        <Route path="/dr_licita" exact component={DrLicita} isPrivate />
        <Route path="/dr_licita/chat" exact component={DrLicitaChat} isPrivate />
        <Route path="/dr_licita/peticoes" exact component={DrLicitaPeticoes} isPrivate />
        <Route path="/dr_licita/projetos" exact component={DrLicitaProjetos} isPrivate />
        <Route
          path="/dr_licita/jurisprudencia"
          exact
          component={DrLicitaJurisprudencia}
          isPrivate
        />
        <Route path="/dr_licita/biblioteca" exact component={DrLicitaBiblioteca} isPrivate />
        <Route path="/dr_licita/document" exact component={DrLicitaCreateDocument} isPrivate />
        <Route path="/cadastrar" component={RegistrationFlow} isPrivate />
        <Route path="/robo_dashboard" component={RoboDashboard} isPrivate />
        <Route path="/monitoring_quoted" component={MonitoringQuoted} isPrivate />
        <Route
          path="/robo_dashboard_user/:id"
          component={(routerProps: RouteComponentProps) => (
            <RobotUserDetails params={routerProps.match.params} />
          )}
        />
        <Route path="/minha_conta" component={MyAccount} isPrivate />
        <Route path="/boletim_web/minha_conta/boletos_online_new" component={MyAccount} isPrivate />
        <Route path="/curso" component={Course} isPrivate />
        <Route path="/conlicitantes" component={Conlicitantes} isPrivate />
        <Route
          path="/conteudo_exclusivo/:page"
          component={(routerProps: RouteComponentProps) => (
            <JobVacancy params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/mercado" exact component={Market} isPrivate />
        <Route
          path="/mercado/bidding/:id"
          exact
          component={(routerProps: RouteComponentProps) => (
            <BiddingMarket params={routerProps.match.params} />
          )}
          isPrivate
        />

        <Route path="/assessoria_cadastral" component={CadastralAdvisory} isPrivate />
        <Route path="/programa_de_integridade" component={ProgramaDeIntegridade} isPrivate />

        <Route path="/conlicitacao_api" exact component={ConlicitacaoApi} isPrivate />
        <Route path="/conlicitacao_api/meus_tokens" exact component={MeusTokens} isPrivate />
        <Route
          path="/conlicitacao_api/solicitar"
          exact
          component={ApiRequestPresentation}
          isPrivate
        />
        <Route
          path="/conlicitacao_api/solicitacao"
          exact
          component={ConlicitacaoApiRequest}
          isPrivate
        />
        <Route path="/conlicita_go" component={ConlicitaGo} isPrivate />
        <Route path="/tools_status" component={ToolsStatus} isPrivate />
        <Route path="/validar_contrato" exact component={GuardedContractValidator} isPrivate />
        <Route
          path="/validar_contrato/criar"
          exact
          component={GuardedNewContractAnalysis}
          isPrivate
        />

        <Route
          path="/validar_contrato/relatorio/:id"
          exact
          component={GuardedDetailedContractAnalysis}
          isPrivate
        />
        <Route path="/juridico" exact component={JuridicoFacil} isPrivate />
        <Route
          path="/juridico/show/:id"
          component={(routerProps: RouteComponentProps) => (
            <JuridicoFacilShow params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route
          path="/juridico/view/:tipo/:id"
          exact
          component={(routerProps: RouteComponentProps) => (
            <JuridicoThemes params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route
          path="/juridico/search/:search/:tipo/:accept/:video/:area?/:tema?/:subtema?"
          component={(routerProps: RouteComponentProps) => (
            <JuridicoSearch params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/legislacao" exact component={Legislation} isPrivate />
        <Route
          path="/legislacao/lei/:id"
          component={(routerProps: RouteComponentProps) => (
            <LegislationLaw params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route
          path="/legislacao/show/:search/:accept?/:unica_lei?/:tipo?"
          component={(routerProps: RouteComponentProps) => (
            <LegislationSearch params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/consultoria_juridica" exact component={ConsultancyLegal} isPrivate />
        <Route
          path="/consultoria_juridica/posts/:id"
          component={(routerProps: RouteComponentProps) => (
            <DicaPost params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/cadastro" exact component={Register} />
        <Route path="/cadastro_novo" exact component={RegisterNew} />
        <Route
          path="/usuarios/password/:page"
          component={(routerProps: RouteComponentProps) => (
            <Passwords params={routerProps.match.params} />
          )}
          isPrivate
        />
        <Route path="/facilitadores/robo" component={RoboDeLances} />
        <Route path="/minhas_licitacoes/shares" component={SharedBidding} />
        <Route path="/modelos-documentos" component={DocumentGenerator} />
        <Route path="/notification/notifications" exact component={Notifications} />
        <Route path="/usuarios/invitation/accept/:invitation_token?" exact component={Invitation} />
        <Route path="/usuarios/recurso_expirado" exact component={Expired} />
        <Route
          path="/boletim_web/public/boletins/:boletin_id/biddings/:id"
          component={(routerProps: RouteComponentProps) => (
            <BiddingDetailsPrBw params={routerProps.match.params} />
          )}
          exact
          isPrivate
        />
        <Route path="/recurso_inativo" component={DisabledService} isPrivate />
        <Route
          path="/assinatura_vencida"
          component={() => <BlockedSubscription paralyzed={false} />}
        />
        <Route path="/assinatura_paralisada" component={() => <BlockedSubscription paralyzed />} />
        <Route path="/renovacao" component={CampaignRenewal} isPrivate />
        <Route path="/renovacao_email" component={RenewalEmail} isPrivate />
        <Route path="/portais" component={Portals} isPrivate />
        <Route path="/palestra" component={Palestra} isPrivate />
        <Route path="/concorrentes" component={ConcurrentsNew} exact isPrivate />
        <Route path="/concorrente/:cnpj" component={ConcurrentsData} exact isPrivate />
        <Route
          path="/representante/meus-clientes"
          component={RepresentantClients}
          exact
          isPrivate
        />
        <Route path="/representante/kanban" component={RepresentantKanbanPage} exact isPrivate />
        <Route
          path="/representante/prospeccao"
          component={RepresentantProspectingPage}
          exact
          isPrivate
        />
        <Route
          path="/representante/fornecedores"
          component={RepresentantSuppliersPage}
          exact
          isPrivate
        />
        <Route path="/representacao/:id" component={RepresentationRequest} exact isPrivate />
        <Route path="/conlicitaseg" exact component={Conlicitaseg} isPrivate />
        <Route
          path="/conlicitaseg/processo/:processId/:quoteId"
          component={ProcessInfo}
          isPrivate
        />
        <Route path="/conlicitaseg/novo" component={BiddingQuoteIntro} isPrivate />
        <Route path="/conlicitaseg/garantia/motivo" component={ReasonsNewInsurance} isPrivate />
        <Route path="/conlicitaseg/garantia/dados" component={ConlicitasegData} isPrivate />
        <Route path="/conlicitaseg/garantia/nova" component={BiddingQuoteStart} isPrivate />

        <Route
          path="/conlicitaseg/garantia/upload-contrato"
          component={BiddingQuoteContractUpload}
          isPrivate
        />
        <Route
          path="/conlicitaseg/garantia/tomador-segurado"
          component={QuoteHolderData}
          isPrivate
        />
        <Route
          path="/conlicitaseg/garantia/licitacao-contrato"
          component={QuoteContractInfo}
          isPrivate
        />
        <LegacyQuoteRedirects />
        <Route path="/conlicitaseg/garantia/confirmacao" component={QuoteConfimation} isPrivate />
        <Route path="/conlicitaseg/cotacoes/:processId" component={QuotesSelection} isPrivate />
        <Route path="/conlicitaseg/pagamento" component={PaymentInfo} isPrivate />
        <Route path="/conlicitaseg/informacoes" component={InsuranceSuccess} isPrivate />
        <Route path="/conlicitaseg/contato" component={ConlicitasegContact} isPrivate />
        <Route
          path="/conlicitaseg/only-onboarding"
          component={ConlicitasegOnlyOnboarding}
          isPrivate
        />
        <Route path="/conlicitaseg/cadastro" exact component={ConlicitasegSignup} />
        <Route path="/conlicitaseg/validar-email" exact component={ConlicitasegEmailValidation} />
        <Route path="/onboarding" exact component={Onboarding} />
        <Route path="/register/pending-approval" exact component={RegisterPendingApproval} />
        <Route path="/register/preparing" exact component={RegisterPreparing} />
        <Route path="/central_ia" exact component={GuardedCentralIA} isPrivate />
        <Route path="/academy/:tab?/:id?" component={Academy} isPrivate />
        <Route path="/embaixadores" exact component={AmbassadorInvitation} isPrivate />
        <Route
          path="/embaixadores/ferramentas/:slug"
          exact
          component={AmbassadorToolDetail}
          isPrivate
        />
        <Route
          path="/embaixadores/ferramentas/:slug/feedback"
          exact
          component={AmbassadorToolFeedback}
          isPrivate
        />
        <Route path="/pesquisa_de_precos" exact component={PriceIntelligence} isPrivate />
        <Route path="/gestao_ia" component={AIManagement} isPrivate />

        <Route
          path="/pesquisa_de_precos/salvas"
          exact
          component={PriceIntelligenceSaved}
          isPrivate
        />
        <Route
          path="/pesquisa_de_precos/salvas/:id"
          exact
          component={PriceIntelligenceSavedDetail}
          isPrivate
        />
        <Route
          path="/pesquisa_de_precos/licitacao/:id"
          exact
          component={(routerProps: RouteComponentProps) => (
            <BiddingMarket
              params={routerProps.match.params}
              voltar={{ to: '/pesquisa_de_precos', label: 'Voltar para a Análise de Mercado 3.0' }}
            />
          )}
          isPrivate
        />
      </Suspense>
      {/* Montado fora das rotas: persiste na navegação (o iframe da live NÃO recarrega a cada página). */}
      {academyEnabled && <AcademyFloatingVideo />}
    </BrowserRouter>
  );
}
